import * as signalR from "@microsoft/signalr";
import lookupDataService from "./LookupDataService";

class SignalRService {
  private connection: signalR.HubConnection | null = null;
  private pendingApprovalsListeners: Array<() => void> = [];

  connect(baseUrl: string) {
    if (this.connection) {
      return;
    }

    // Received as a parameter (rather than read from localStorage here) because
    // logout clears localStorage entirely and nothing repopulates "baseUrl" without
    // a full page reload - the caller's value (window.VUE_APP_API_KEY) survives that.
    // Browsers can't attach custom headers (e.g. X-Tenant-ID) to a native WebSocket
    // handshake, so the tenant is passed as a query string instead, which does survive it.
    const tenantId = window.location.hostname.split(".")[0].toUpperCase();
    const hubUrl = baseUrl + "hubs/lookupdata?tenantId=" + encodeURIComponent(tenantId);
    // Debug logging kept for diagnosing connection issues - uncomment if needed.
    // console.log("[SignalRService] connecting to", hubUrl);

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => localStorage.getItem("token") ?? "",
      })
      .withAutomaticReconnect()
      // Default log level is Information, which logs the hub URL (including the
      // access token in the query string) to the console on every connection attempt.
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    const refreshLookupData = (source: string) => {
      // console.log(`[SignalRService] refreshLookupData triggered by ${source} at ${new Date().toISOString()}`);
      const token = localStorage.getItem("token") ?? "";
      lookupDataService.fetchDataOnLogin(baseUrl, token).catch((error) => {
        console.error("Failed to refresh lookup data after SignalR signal:", error);
      });
    };

    // Echoes the resolved tenant back to the caller - useful for verifying tenant
    // resolution from the browser console without needing server-side log access.
    // this.connection.on("TenantResolved", (tenantId: string) => {
    //   console.log("[SignalRService] hub resolved this connection's tenantId as:", tenantId);
    // });
    this.connection.on("LookupDataChanged", () => refreshLookupData("LookupDataChanged event"));
    this.connection.on("PendingApprovalsChanged", () => {
      this.pendingApprovalsListeners.forEach((listener) => listener());
    });
    // SignalR does not replay missed messages, so treat every reconnect as stale.
    this.connection.onreconnected(() => {
      refreshLookupData("onreconnected");
      this.pendingApprovalsListeners.forEach((listener) => listener());
    });
    // this.connection.onreconnecting((error) => console.log("[SignalRService] reconnecting", error));
    // this.connection.onclose((error) => console.log("[SignalRService] connection closed", error));

    this.connection.start()
      // .then(() => {
      //   console.log("[SignalRService] connected, connectionId=", this.connection?.connectionId, "state=", this.connection?.state);
      // })
      .catch((error) => {
        console.error("Failed to start SignalR connection:", error);
      });
  }

  // Lets any mounted component (nav badge, pending-approvals list) react to a
  // holiday request being submitted/approved/rejected without polling. Returns
  // an unsubscribe function; listeners survive disconnect()/connect() cycles.
  onPendingApprovalsChanged(listener: () => void): () => void {
    this.pendingApprovalsListeners.push(listener);
    return () => {
      this.pendingApprovalsListeners = this.pendingApprovalsListeners.filter((l) => l !== listener);
    };
  }

  disconnect() {
    if (!this.connection) {
      return;
    }

    this.connection.stop().catch((error) => {
      console.error("Failed to stop SignalR connection:", error);
    });
    this.connection = null;
  }
}

export default new SignalRService();
